/**
 * Admin Mode Service
 * Handles temporary admin privilege elevation via Cloud Functions
 */

import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../config/firebase';

export interface AdminModeRequest {
  adminType: 'system' | 'org';
  orgId?: string;
}

export interface AdminModeResponse {
  success: boolean;
  adminType: 'system' | 'org';
  orgId?: string;
  expiresAt: number;
  durationMs: number;
  message: string;
}

export interface DisposeAdminModeResponse {
  success: boolean;
  message: string;
}

/**
 * Request temporary admin mode privileges
 * @param request - Admin mode request parameters
 * @returns Promise with admin mode response
 */
export async function requestAdminMode(
  request: AdminModeRequest
): Promise<AdminModeResponse> {
  try {
    const requestAdminModeFunction = httpsCallable<
      AdminModeRequest,
      AdminModeResponse
    >(functions, 'requestAdminMode');

    const result = await requestAdminModeFunction(request);
    return result.data;
  } catch (error) {
    console.error('Error requesting admin mode:', error);
    throw error;
  }
}

/**
 * Dispose of admin mode privileges early
 * @returns Promise with dispose response
 */
export async function disposeAdminMode(): Promise<DisposeAdminModeResponse> {
  try {
    const disposeAdminModeFunction = httpsCallable<
      void,
      DisposeAdminModeResponse
    >(functions, 'disposeAdminMode');

    const result = await disposeAdminModeFunction();
    return result.data;
  } catch (error) {
    console.error('Error disposing admin mode:', error);
    throw error;
  }
}

/**
 * Parse admin mode info from ID token claims
 */
export interface AdminModeInfo {
  isActive: boolean;
  type: 'system' | 'org' | null;
  orgId?: string;
  expiresAt: number | null;
  timeRemaining: number | null;
  isExpired: boolean;
}

export function parseAdminModeFromClaims(claims: {
  admin?: boolean | 'system' | 'org';
  admin_expires_ms?: number;
  org_id?: string;
}): AdminModeInfo {
  const now = Date.now();

  // Check if admin claim exists
  if (!claims.admin) {
    return {
      isActive: false,
      type: null,
      expiresAt: null,
      timeRemaining: null,
      isExpired: false,
    };
  }

  // No valid admin claim found  // Handle temporary admin mode
  const adminType = claims.admin as 'system' | 'org';
  const expiresAt = claims.admin_expires_ms || 0;
  const isExpired = expiresAt > 0 && expiresAt < now;
  const timeRemaining = expiresAt > 0 ? Math.max(0, expiresAt - now) : null;

  return {
    isActive: !isExpired,
    type: adminType,
    orgId: claims.org_id,
    expiresAt,
    timeRemaining,
    isExpired,
  };
}

/**
 * Check if user is eligible for system admin mode
 * @param userId - User ID to check
 * @returns Promise<boolean> - True if user is in siteAdmin collection
 */
export async function checkSiteAdminEligibility(
  userId: string
): Promise<boolean> {
  try {
    const siteAdminDoc = await getDoc(doc(db, 'siteAdmin', userId));
    return siteAdminDoc.exists();
  } catch (error) {
    console.error('Error checking site admin eligibility:', error);
    return false;
  }
}

/**
 * Check if user has active organization admin mode for a specific organization
 * @param currentUser - Firebase User object
 * @param organizationId - Organization ID to check
 * @returns Promise<boolean> - True if user has active org admin mode for this organization
 */
export async function hasOrgAdminMode(
  currentUser: { getIdTokenResult: () => Promise<{ claims: Record<string, unknown> }> } | null,
  organizationId: string
): Promise<boolean> {
  if (!currentUser || !organizationId) return false;

  try {
    const tokenResult = await currentUser.getIdTokenResult();
    return (
      tokenResult.claims.admin === 'org' &&
      tokenResult.claims.org_id === organizationId &&
      typeof tokenResult.claims.admin_expires_ms === 'number' &&
      tokenResult.claims.admin_expires_ms > Date.now()
    );
  } catch (error) {
    console.error('Error checking org admin mode:', error);
    return false;
  }
}

/**
 * Check if user has system admin privileges
 * @param currentUser - Firebase User object
 * @returns Promise<boolean> - True if user has system admin privileges
 */
export async function hasSystemAdminMode(
  currentUser: { getIdTokenResult: () => Promise<{ claims: Record<string, unknown> }> } | null
): Promise<boolean> {
  if (!currentUser) return false;

  try {
    const tokenResult = await currentUser.getIdTokenResult();
    return tokenResult.claims.admin === 'system';
  } catch (error) {
    console.error('Error checking system admin:', error);
    return false;
  }
}
